import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { foundation } from '../../ui';
import { tint, semantic as sem } from '../../ui/theme/semanticTokens';
import { C, AUTH_CONTROL_RADIUS } from './loginAuthTheme';
import useReducedMotion from '../../hooks/ai/useReducedMotion';
import useOtpDigitMotion from './useOtpDigitMotion';
import OtpMotionLayer, { useOtpCellRects } from './OtpMotionLayer';
import './mfaOtpMotion.css';

/**
 * Resting cell fill — accent-50, deliberately NOT slate-100.
 *
 * This was slate-100 (#f1f5f9), which looked right on the white login card but
 * was INVISIBLE in Account → Security: AccountRow's expanded row background is
 * `neutral.surfaceMuted`, which resolves to slate-100 — the exact same colour.
 * Five of the six cells disappeared into the background and only the focused
 * one (blue tint + ring) could be seen, which read as "only one box renders".
 * All six inputs were always present at the correct 47px; they were camouflaged,
 * never clipped.
 *
 * A faint blue reads clearly on BOTH surfaces, so login and settings stay
 * genuinely identical rather than two lookalikes that drift.
 *
 * If you change this, check it against a white card AND a slate-100 surface.
 * Never set it to a token that any container background also uses.
 */
const REST_BG = tint.otpCell;

// Static cell chrome hoisted out of render — only border/boxShadow/height/font
// vary per cell, computed inline below. Avoids re-allocating 6 full style
// objects on every keystroke.
const CELL_FONT_SIZE = 'clamp(1rem, 3.5vw, 1.125rem)';
const CELL_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
const BASE_CELL_STYLE = {
  flex: '1 1 0',
  // Exposed as a variable so the digit-enter keyframes (mfaOtpMotion.css) can start
  // the glyph at 60% of the live size — the input box itself never scales.
  '--cvx-otp-fs': CELL_FONT_SIZE,
  minWidth: '36px',
  maxWidth: '48px',
  textAlign: 'center',
  fontSize: 'var(--cvx-otp-fs)',
  fontWeight: 600,
  color: C.heading,
  // Outline-free resting look (founder call 2026-07-24): soft fill, no
  // border — the active cell carries a brand ring via box-shadow only.
  backgroundColor: REST_BG,
  border: 'none',
  borderRadius: AUTH_CONTROL_RADIUS,
  outline: 'none',
  boxSizing: 'border-box',
  transition: [
    `background-color 140ms ${CELL_EASE}`,
    `box-shadow 140ms ${CELL_EASE}`,
    `border-radius 350ms ${CELL_EASE}`,
    `color 200ms ${CELL_EASE}`,
  ].join(', '),
};
// Focused cell LIFTS to white under the brand ring. With a tinted resting fill,
// a tinted focus fill would be nearly the same colour — white is what makes the
// active cell read as raised rather than merely a slightly different blue.
const FOCUS_BG = C.white;
const FOCUS_SHADOW = `0 0 0 2px ${C.focusBorder}`;

const DANGER = foundation.color.semantic.danger;

/* Status chrome — error flushes the cells red (parent clears the value);
   verified goes green for the success beat before the app opens. */
const STATUS_CELL = {
  error: { backgroundColor: sem.tint.rose50, color: DANGER, boxShadow: `0 0 0 1.5px ${DANGER} inset` },
  verified: { backgroundColor: C.successBg, color: C.success },
};

const GROUP_STYLE = {
  // Containing block for the gliding ring + exit ghosts (absolute, so they
  // never become flex items and never move a cell).
  position: 'relative',
  display: 'flex',
  gap: 'clamp(4px, 1.8vw, 10px)',
  justifyContent: 'center',
  flexWrap: 'nowrap',
  width: '100%',
  maxWidth: '320px',
  marginLeft: 'auto',
  marginRight: 'auto',
};

// Visual-only digit box. aria-hidden: the ONE real <input> over the row is the
// accessible control; the digit is just this div's text.
const OtpCell = memo(function OtpCell({
  index, digit, focused, inputH, sans, status, fit, ringGlides, enterDelay, enterGen, onCellRef,
}) {
  const statusStyle = STATUS_CELL[status] || null;
  // Enter beat: two identical keyframe names alternate by generation so a
  // retyped cell restarts its animation without remounting.
  const entering = enterDelay != null;
  return (
    <div
      aria-hidden="true"
      data-otp-cell={index}
      ref={(el) => onCellRef(index, el)}
      className={entering ? `cvx-otp-digit-in--${enterGen % 2}` : undefined}
      style={{
        ...BASE_CELL_STYLE,
        ...(fit || null),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        pointerEvents: 'none',
        height: inputH,
        fontFamily: sans,
        backgroundColor: focused && !statusStyle ? FOCUS_BG : BASE_CELL_STYLE.backgroundColor,
        boxShadow: focused && !statusStyle && !ringGlides ? FOCUS_SHADOW : 'none',
        ...(entering && enterDelay ? { animationDelay: `${enterDelay}ms` } : null),
        ...(statusStyle || null),
      }}
    >
      {digit}
    </div>
  );
});

// The real input: stretched, transparent, but NEVER invisible (opacity 0 /
// display none / visibility hidden make password managers and iOS ignore it).
// 16px font so iOS never zooms on focus. Pinned here (and opted out of the global touch
// field-size rule with data-cvx-field-text="fixed", KAN-969): its text is transparent and the
// six boxes above carry the digits, so the size must never follow the field token.
const INPUT_STYLE = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 1,
  margin: 0,
  padding: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: 'transparent',
  caretColor: 'transparent',
  WebkitTextFillColor: 'transparent',
  fontSize: '16px',
  opacity: 1,
  WebkitTapHighlightColor: 'transparent',
  appearance: 'none',
  WebkitAppearance: 'none',
  textAlign: 'left',
  letterSpacing: 0,
  cursor: 'text',
  boxSizing: 'border-box',
};

/** Sign-in cell floor (36px) + fluid gap — `MfaOtpRow` reads these to know when 6 cells can't fit. */
export const OTP_CELL_MIN_PX = 36;
export const otpGapPx = (viewportW) => Math.min(10, Math.max(4, viewportW * 0.018));

/**
 * Size-only fit for narrow containers (`fitScale` < 1, MFA modals on a 320px
 * phone). Scales cell width bounds, gap and glyph size by ONE factor; every
 * other property (fill, radius, ring, font) stays the sign-in look. The
 * caller scales `inputH` itself. Login never passes it → `null` → untouched.
 */
function fitStyles(fitScale) {
  if (!(fitScale > 0 && fitScale < 1)) return null;
  const k = fitScale.toFixed(4);
  const fs = `calc(${CELL_FONT_SIZE} * ${k})`;
  return {
    cell: { '--cvx-otp-fs': fs, minWidth: `${(OTP_CELL_MIN_PX * fitScale).toFixed(2)}px`, maxWidth: `${(48 * fitScale).toFixed(2)}px` },
    gap: `calc(${GROUP_STYLE.gap} * ${k})`,
    fontSize: fs,
  };
}

/**
 * What ONE change event inserted, and whether we know it EXACTLY (KAN-616).
 * `caret` = selectionStart after the change (the end of the inserted text);
 * `sel` = the [start, end] selection just before it (null if unknown).
 *  1. Selection-anchored: prev[0..start) + inserted + prev[end..] — exact.
 *  2. Pure insertion (nothing replaced): the inserted length is
 *     raw.length - prev.length and it ends at the caret — exact when the head
 *     and tail both match prev.
 *  3. Otherwise a prefix/suffix guess, which a repeated digit can shift — the
 *     caller must never auto-verify it.
 * Returns inserted DIGITS only.
 */
export function findOtpInsertion(prev, raw, caret, sel) {
  const digits = (str) => str.replace(/\D/g, '');
  if (caret != null && sel && sel[0] <= sel[1] && sel[0] <= caret
    && raw.slice(0, sel[0]) === prev.slice(0, sel[0])
    && raw.slice(caret) === prev.slice(sel[1])) {
    return { inserted: digits(raw.slice(sel[0], caret)), exact: true };
  }
  const start = caret == null ? -1 : caret - (raw.length - prev.length);
  if (start >= 0 && start <= caret && raw.slice(0, start) === prev.slice(0, start) && raw.slice(caret) === prev.slice(start)) {
    return { inserted: digits(raw.slice(start, caret)), exact: true };
  }
  let p = 0;
  const max = Math.min(raw.length, prev.length);
  while (p < max && raw[p] === prev[p]) p += 1;
  let s = 0;
  while (s < max - p && raw[raw.length - 1 - s] === prev[prev.length - 1 - s]) s += 1;
  return { inserted: digits(raw.slice(p, raw.length - s)), exact: false };
}

/**
 * Six visual digit boxes over ONE real `<input>` (KAN-616, 2026-10-03).
 *
 * This was six separate inputs that intercepted paste (preventDefault and
 * a manual clipboard read) and hopped focus between boxes. In Brave on iPhone a
 * long-press -> Paste on box 1 did nothing (Brave never hands the page the
 * clipboard; only its "Force Paste" works) and the focus hopping — including a
 * redirect inside the focus handler — dropped the keyboard in non-Safari iPhone
 * browsers. The standard OTP pattern fixes both: one transparent input (the
 * browser owns typing, paste, autofill, long-press menus) stretched over six
 * `aria-hidden` boxes that only DISPLAY its value. We never read the clipboard:
 * a paste or autofill simply arrives as a change event, which we diff against
 * the previous value to find what was inserted.
 *
 * `onWholeCode(code)` (optional, RD-SEC-019): fires ONLY when EXACTLY 6
 * digits arrive in ONE change event — a paste, or an OS / password manager
 * autofill (7+ digits fill the row but never verify; a 6-digit chunk inserted
 * into an already-filled row replaces it and verifies). A code that arrives at
 * once has nothing for the user to review, so consumers may opt in to verify
 * right away. It never fires for typed digits, partial pastes (even ones that
 * complete the row), Backspace or Enter, nor while `disabled`. `onFilled`
 * still fires for every route to a full row.
 *
 * `autoFocus` (default true, KAN-620): false keeps the cursor out of the row
 * on mount. Pop-ups that open straight onto a code pass false, so Safari's
 * Passwords code list (it opens on focus of a one-time-code field) waits for
 * a click instead of covering the warning text and the confirm button.
 */
export default function MfaOtpInputs({ value, onChange, disabled, inputH, sans, onFilled, onWholeCode, onSubmit, groupLabel, status = 'idle', describedById, fitScale, autoFocus = true }) {
  const refs = useRef([]);
  const groupRef = useRef(null);
  const inputRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const reduced = useReducedMotion();
  // Status chrome (error shake/red, verified green) and reduced motion both
  // win over the digit beats — no class, no ghost, no cascade.
  const motion = useOtpDigitMotion(value, !reduced && !STATUS_CELL[status]);
  const rects = useOtpCellRects(groupRef, refs, inputH);
  const ringGlides = !!rects?.[focusedIndex]?.w;
  const fit = useMemo(() => fitStyles(fitScale), [fitScale]);

  const valueRef = useRef(value);
  valueRef.current = value;
  // Caret computed in onChange, applied after React commits the new value.
  const pendingSelRef = useRef(null);
  // The selection as it stood BEFORE the next change, so findOtpInsertion can
  // tell exactly what a paste replaced. null = unknown (falls back safely).
  const lastSelRef = useRef(null);
  const noteSel = useCallback((el) => {
    lastSelRef.current = el && el.selectionStart != null ? [el.selectionStart, el.selectionEnd ?? el.selectionStart] : null;
  }, []);

  const onCellRef = useCallback((i, el) => { refs.current[i] = el; }, []);

  const ringFrom = useCallback((start, len) => {
    setFocusedIndex(Math.max(0, Math.min(start ?? 0, 5, len)));
  }, []);

  const select = useCallback((a, b) => {
    const el = inputRef.current;
    if (!el) return;
    try { el.setSelectionRange(a, b); } catch (e) { /* unsupported input state */ }
    noteSel(el);
    ringFrom(a, valueRef.current.length);
  }, [ringFrom, noteSel]);

  const caretAtEnd = useCallback(() => {
    const len = valueRef.current.length;
    if (len >= 6) select(5, 6); else select(len, len);
  }, [select]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    const sel = pendingSelRef.current;
    pendingSelRef.current = null;
    if (!el || typeof document === 'undefined' || document.activeElement !== el) return;
    if (sel) {
      try { el.setSelectionRange(sel[0], sel[1]); } catch (e) { /* ignore */ }
      ringFrom(sel[0], value.length);
    } else {
      ringFrom(el.selectionStart, value.length);
    }
    noteSel(el);
  }, [value, ringFrom, noteSel]);

  const handleChange = useCallback((e) => {
    const prev = valueRef.current;
    const raw = e.target.value;
    const caret = e.target.selectionStart;
    const { inserted, exact } = findOtpInsertion(prev, raw, caret, lastSelRef.current);
    if (inserted.length >= 6) {
      // A whole code replaces the row. Only an EXACTLY-known insertion may
      // auto-verify: a guessed boundary ("1" + caret at 0 + paste "123456"
      // could read as "234561") fills the row as typed and waits for Verify
      // (security + code review, KAN-616).
      const next = (exact ? inserted : raw.replace(/\D/g, '')).slice(0, 6);
      pendingSelRef.current = [5, 6];
      onChange(next);
      onFilled(next);
      if (exact && inserted.length === 6 && !disabled) onWholeCode?.(next);
      return;
    }
    const next = raw.replace(/\D/g, '').slice(0, 6);
    if (next.length < prev.length) {
      const c = Math.min(caret ?? next.length, next.length);
      pendingSelRef.current = [c, c];
    } else {
      const c = Math.min(caret ?? next.length, next.length);
      if (c < next.length) pendingSelRef.current = [c, c + 1];
      else if (next.length === 6) pendingSelRef.current = [5, 6];
      else pendingSelRef.current = [next.length, next.length];
    }
    if (next !== prev) {
      onChange(next);
      if (next.length === 6) onFilled(next);
    } else {
      pendingSelRef.current = null;
      lastSelRef.current = null; // React restores the DOM value; caret unknown
    }
  }, [onChange, onFilled, onWholeCode, disabled]);

  const handleClick = useCallback((e) => {
    const len = valueRef.current.length;
    let best = 0;
    let bestD = Infinity;
    refs.current.slice(0, 6).forEach((el, i) => {
      const r = el?.getBoundingClientRect?.();
      if (!r) return;
      const d = e.clientX >= r.left && e.clientX <= r.right ? 0 : Math.min(Math.abs(e.clientX - r.left), Math.abs(e.clientX - r.right));
      if (d < bestD) { bestD = d; best = i; }
    });
    if (best < len) select(best, best + 1);
    else caretAtEnd();
  }, [select, caretAtEnd]);

  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    const cur = valueRef.current;
    if (cur.length === 6 && onSubmit) onSubmit(cur);
    e.preventDefault();
  }, [onSubmit]);

  // Autofocus on mount — hover + fine-pointer devices only (a hardware
  // keyboard is likely; on touch it would force the on-screen keyboard open).
  // Guarded against StepCrossfade's GHOST-MOUNT CONTRACT: the leaving snapshot
  // is a REAL second mount of this component — never steal focus into it.
  useEffect(() => {
    if (!autoFocus || typeof window === 'undefined' || !window.matchMedia) return;
    const el = inputRef.current;
    if (!el || el.closest('[data-testid="auth-step-leaving"]')) return;
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) el.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={groupRef}
      role="group"
      data-auth-stagger=""
      data-otp-status={status}
      aria-label={groupLabel}
      aria-describedby={describedById}
      style={{
        ...GROUP_STYLE,
        ...(fit ? { gap: fit.gap } : null),
        animation: status === 'error' && !reduced ? 'cvx-input-shake 400ms cubic-bezier(0.36, 0.07, 0.19, 0.97)' : 'none',
      }}
    >
      {Array.from({ length: 6 }, (_, i) => (
        <OtpCell
          key={i}
          index={i}
          digit={value[i] ?? ''}
          focused={focusedIndex === i}
          inputH={inputH}
          sans={sans}
          status={status}
          fit={fit?.cell}
          ringGlides={ringGlides}
          enterDelay={motion.enter[i]}
          enterGen={motion.enter[i] != null ? motion.gen : 0}
          onCellRef={onCellRef}
        />
      ))}
      <OtpMotionLayer
        rects={rects}
        focusedIndex={focusedIndex}
        showRing={!STATUS_CELL[status]}
        ringShadow={FOCUS_SHADOW}
        radius={AUTH_CONTROL_RADIUS}
        reduced={reduced}
        ghosts={motion.exit}
        gen={motion.gen}
        font={{ fontFamily: sans, fontSize: fit?.fontSize || CELL_FONT_SIZE, fontWeight: 600, color: C.heading }}
      />
      <input
        id="mfa-otp-0"
        data-cvx-focus-ring="off"
        data-cvx-field-text="fixed"
        ref={inputRef}
        className="cvx-otp-input"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        value={value}
        disabled={disabled}
        aria-label={groupLabel}
        aria-describedby={describedById}
        aria-invalid={status === 'error' || undefined}
        onChange={handleChange}
        onClick={handleClick}
        onFocus={caretAtEnd}
        onSelect={(e) => { noteSel(e.target); ringFrom(e.target.selectionStart, valueRef.current.length); }}
        onKeyDown={handleKeyDown}
        style={INPUT_STYLE}
      />
    </div>
  );
}
